import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Scale, Send, Loader2, History, Lightbulb, ExternalLink,
  Trash2, MessageSquare, Sparkles, Users, Search, RefreshCw,
  Building2, ShieldCheck, ShieldAlert, Briefcase, Plus, Pencil,
  Globe, FileSignature,
} from "lucide-react";

/**
 * Fair Work HR Compliance Assistant.
 *
 * Vault-gated page where admins describe an employee or contractor
 * in plain English and get back compliance guidance from a Claude
 * Sonnet 4.5 assistant pre-prompted on Australian employment law,
 * NES, modern awards, and Fair Work obligations.
 */
export default function FairWorkPage() {
  const [description, setDescription] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [current, setCurrent] = useState(null);  // last { description, answer, query_id }
  const [history, setHistory] = useState([]);
  const [tips, setTips] = useState({ tips: [], examples: [] });
  const [asking, setAsking] = useState(false);
  const [polling, setPolling] = useState(false);
  const [followUpMode, setFollowUpMode] = useState(false);
  // Anonymisation hint — surname + first name of the KeyPay-picked
  // worker. Passed on the ask body so the backend can strip the
  // surname from every LLM prompt (keeps the admin-visible history
  // full-name but the AI itself only sees a first name).
  const [pickedEmployee, setPickedEmployee] = useState(null);

  // KeyPay-employee browser state.
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [empSearch, setEmpSearch] = useState("");
  const [empShowTerminated, setEmpShowTerminated] = useState(false);

  // Contractors register state.
  const [contractors, setContractors] = useState([]);
  const [contractorsLoading, setContractorsLoading] = useState(false);
  const [contractorSearch, setContractorSearch] = useState("");
  const [contractorOffshoreCount, setContractorOffshoreCount] = useState(0);
  const [contractorEditing, setContractorEditing] = useState(null); // null | {} | object
  const [contractorSaving, setContractorSaving] = useState(false);

  // FWC Modern Awards Pay Database API status.
  const [fwc, setFwc] = useState(null);
  const [fwcOpen, setFwcOpen] = useState(false);
  const [fwcSaving, setFwcSaving] = useState(false);
  const [fwcKey, setFwcKey] = useState("");
  const [fwcBaseUrl, setFwcBaseUrl] = useState("https://api.fwc.gov.au");
  const [fwcEnabled, setFwcEnabled] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/fairwork/queries", { params: { limit: 25 } });
      setHistory(data.queries || []);
    } catch (e) {
      // History is non-critical — keep the page useful even if it fails.
      logger.error("[FairWork] failed to load history:", e);
    }
  }, []);

  useEffect(() => {
    loadHistory();
    api.get("/fairwork/prompt-tips").then((r) => setTips(r.data))
      .catch((e) => logger.warn("[FairWork] prompt-tips:",
                                  e?.response?.data || e));
  }, [loadHistory]);

  // ── KeyPay employees (pooled across every connected client) ─────
  const loadEmployees = useCallback(async (refresh = false) => {
    setEmployeesLoading(true);
    try {
      const { data } = await api.get("/fairwork/keypay-employees",
        { params: refresh ? { refresh: true } : {} });
      setEmployees(data.employees || []);
    } catch (e) {
      logger.error("[FairWork] failed to load employees:", e);
    } finally { setEmployeesLoading(false); }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  // ── Contractors register (persisted in `fairwork_contractors`) ──
  const loadContractors = useCallback(async () => {
    setContractorsLoading(true);
    try {
      const { data } = await api.get("/fairwork/contractors");
      setContractors(data.contractors || []);
      setContractorOffshoreCount(data.offshore_count || 0);
    } catch (e) {
      logger.error("[FairWork] failed to load contractors:", e);
    } finally { setContractorsLoading(false); }
  }, []);

  useEffect(() => { loadContractors(); }, [loadContractors]);

  const saveContractor = async (formData) => {
    setContractorSaving(true);
    try {
      const isEdit = !!(formData && formData.contractor_id);
      const url = isEdit
        ? `/fairwork/contractors/${formData.contractor_id}`
        : "/fairwork/contractors";
      const method = isEdit ? "put" : "post";
      // Strip our client-side-only fields before shipping to backend.
      const {
        contractor_id: _cid,
        created_at: _ca, updated_at: _ua,
        created_by: _cb, updated_by: _ub,
        ...payload
      } = formData;
      await api[method](url, payload);
      toast.success(isEdit ? "Contractor updated" : "Contractor added");
      setContractorEditing(null);
      loadContractors();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setContractorSaving(false); }
  };

  const deleteContractor = async (contractor_id) => {
    if (!window.confirm("Delete this contractor from the register?")) return;
    try {
      await api.delete(`/fairwork/contractors/${contractor_id}`);
      toast.success("Contractor removed");
      loadContractors();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ── Click a contractor to pre-fill the compliance description ────
  const pickContractor = (c) => {
    const lines = [];
    // First name only in the description body — surname flows via the
    // ask body's `employee_surname` field for defensive backend redaction.
    const first = c.contact_first_name || c.business_name;
    lines.push(`Contractor engagement — ${c.business_name}`);
    lines.push(`Primary contact: ${first} (anonymised — surname withheld from AI)`);
    if (c.role) lines.push(`Engaged as: ${c.role}`);
    if (c.engagement_entity) lines.push(`Entity type: ${c.engagement_entity}`);
    if (c.country) lines.push(`Country of engagement: ${c.country}${c.state ? ` (${c.state})` : ""}`);
    if (c.abn) lines.push(`ABN: ${c.abn}`);
    if (c.hours_per_week != null) lines.push(`Hours per week (approx): ${c.hours_per_week}`);
    if (c.pay_pattern) lines.push(`Pay pattern: ${c.pay_pattern}`);
    if (c.invoice_pattern) lines.push(`Invoice pattern: ${c.invoice_pattern}`);
    if (c.uses_own_equipment != null) lines.push(`Uses own equipment: ${c.uses_own_equipment ? "Yes" : "No"}`);
    if (c.works_exclusively_for_us != null)
      lines.push(`Works exclusively for us: ${c.works_exclusively_for_us ? "Yes" : "No"}`);
    if (c.engaged_since) lines.push(`Engaged since: ${c.engaged_since}`);
    if (c.notes) lines.push(`Notes: ${c.notes}`);
    lines.push("");
    lines.push("Please assess whether this contractor would be deemed an employee "
      + "under the ATO / Fair Work multi-factor tests (sham contracting risk). "
      + "Call out: (a) workers' comp / superannuation / payroll tax we would be "
      + "liable for if reclassified, (b) long-service leave exposure, (c) whether "
      + "the invoicing pattern is a red flag, and (d) — because the country is "
      + `"${c.country}" — flag whether this engagement counts as an "Australian" or `
      + "overseas contractor for R&D Tax Incentive purposes.");
    setDescription(lines.join("\n"));
    setCurrent(null);
    setFollowUpMode(false);
    setPickedEmployee({
      first_name: c.contact_first_name || null,
      surname: c.contact_surname || null,
    });
    setTimeout(() => {
      document.querySelector('[data-testid="fairwork-description"]')?.focus();
    }, 50);
  };

  const filteredContractors = useMemo(() => {
    const q = contractorSearch.trim().toLowerCase();
    if (!q) return contractors;
    return contractors.filter((c) => {
      const hay = `${c.business_name} ${c.contact_first_name || ""} `
        + `${c.contact_surname || ""} ${c.role || ""} ${c.country || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contractors, contractorSearch]);

  // ── FWC API config (Modern Awards Pay Database) ─────────────────
  const loadFwc = useCallback(async () => {
    try {
      const { data } = await api.get("/fairwork/fwc-config");
      setFwc(data);
      setFwcBaseUrl(data?.base_url || "https://api.fwc.gov.au");
      setFwcEnabled(data?.enabled ?? true);
    } catch (e) { logger.error("[FairWork] failed to load FWC config:", e); }
  }, []);
  useEffect(() => { loadFwc(); }, [loadFwc]);

  const saveFwc = async () => {
    setFwcSaving(true);
    try {
      const body = { base_url: fwcBaseUrl, enabled: fwcEnabled };
      if (fwcKey.trim()) body.api_key = fwcKey.trim();
      await api.put("/fairwork/fwc-config", body);
      setFwcKey("");
      await loadFwc();
      toast.success("FWC API config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setFwcSaving(false); }
  };

  const testFwc = async () => {
    setFwcSaving(true);
    try {
      const { data } = await api.post("/fairwork/fwc-config/test");
      const ok = data?.ok;
      (ok ? toast.success : toast.error)(
        `FWC ping ${data?.status_code ?? "n/a"} — ${(data?.message || "").slice(0, 120)}`);
      await loadFwc();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Probe failed");
    } finally { setFwcSaving(false); }
  };

  // Poll a pending query until it's done/error. Backend kicks off
  // the Claude call as a BackgroundTask (avoids 60s ingress timeout).
  const pollQuery = useCallback(async (queryId) => {
    setPolling(true);
    const started = Date.now();
    const maxMs = 180_000; // 3 minutes — Claude HR answers run 30-90s
    try {
      while (Date.now() - started < maxMs) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data } = await api.get(`/fairwork/queries/${queryId}`);
          if (data?.status === "done" || data?.status === "error") {
            setCurrent(data);
            if (data.status === "done") toast.success("Compliance guidance ready");
            else toast.error(data.answer || "Assistant call failed");
            loadHistory();
            return;
          }
          setCurrent(data); // keep latest placeholder so UI shows status
        } catch (e) {
          // Transient — keep polling unless it's a 4xx
          if (e.response?.status && e.response.status < 500) throw e;
        }
      }
      toast.error("Assistant timed out after 3 minutes — try again");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Polling failed");
    } finally {
      setPolling(false);
    }
  }, [loadHistory]);

  const ask = async () => {
    if (!followUpMode && description.trim().length < 20) {
      return toast.error("Add a few more details about the worker (≥20 chars).");
    }
    if (followUpMode && followUp.trim().length < 5) {
      return toast.error("Type a follow-up question.");
    }
    setAsking(true);
    try {
      const body = followUpMode
        ? { description: current?.description || "(follow-up)",
            follow_up: followUp,
            previous_query_id: current?.query_id,
            employee_first_name: pickedEmployee?.first_name,
            employee_surname: pickedEmployee?.surname }
        : { description,
            employee_first_name: pickedEmployee?.first_name,
            employee_surname: pickedEmployee?.surname };
      const { data } = await api.post("/fairwork/ask", body);
      setCurrent(data);
      setFollowUp("");
      setFollowUpMode(true); // subsequent questions are follow-ups
      loadHistory();
      // Backend returns the placeholder immediately; Claude call runs
      // as a BackgroundTask. Poll for the answer.
      if (data?.query_id && data?.status !== "done") {
        pollQuery(data.query_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assistant call failed");
    } finally { setAsking(false); }
  };

  const startNew = () => {
    setCurrent(null);
    setDescription("");
    setFollowUp("");
    setFollowUpMode(false);
    setPickedEmployee(null);
  };

  const loadFromHistory = async (queryId) => {
    try {
      const { data } = await api.get(`/fairwork/queries/${queryId}`);
      setCurrent(data);
      setDescription(data.description || "");
      setFollowUp("");
      setFollowUpMode(true);
    } catch (e) {
      toast.error("Failed to load that query");
    }
  };

  const removeFromHistory = async (queryId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this Q&A from the audit log?")) return;
    try {
      await api.delete(`/fairwork/queries/${queryId}`);
      if (current?.query_id === queryId) startNew();
      loadHistory();
      toast.success("Deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  // ── Click an employee to pre-fill the compliance description ─────
  const pickEmployee = async (emp) => {
    try {
      // Pull richer profile + standard hours so we can ground the LLM.
      const { data } = await api.get(
        `/fairwork/keypay-employees/${emp.client_id}/${emp.employee_id}`);
      const p = data.profile || {};
      const sh = data.standard_hours || {};
      const lines = [];
      // Only the first name goes into the description so the client's
      // last name never leaves the browser. The backend also strips
      // the surname defensively via `employee_surname` on the ask body.
      const firstNameForDisplay = (emp.first_name
        || p.firstName
        || (emp.name || "").split(" ")[0]
        || "Employee");
      lines.push(`Employee: ${firstNameForDisplay} (anonymised — surname withheld from AI)`);
      if (emp.job_title || p.jobTitle) lines.push(`Role: ${emp.job_title || p.jobTitle}`);
      if (emp.employment_type || p.employmentType)
        lines.push(`Employment type: ${emp.employment_type || p.employmentType}`);
      const status = emp.is_active ? "Active" : `Terminated${emp.end_date ? ` (${emp.end_date.slice(0,10)})` : ""}`;
      lines.push(`Status: ${status}`);
      if (emp.start_date || p.startDate)
        lines.push(`Start date: ${(emp.start_date || p.startDate || "").slice(0,10)}`);
      if (p.dateOfBirth) lines.push(`Date of birth: ${p.dateOfBirth.slice(0,10)} (juniors?)`);
      if (p.residentialState || p.residentialAddressState)
        lines.push(`State: ${p.residentialState || p.residentialAddressState}`);
      if (sh?.hoursPerWeek != null) lines.push(`Standard hours: ${sh.hoursPerWeek}h / week`);
      if (sh?.numberOfHoursPerDay != null) lines.push(`Hours/day: ${sh.numberOfHoursPerDay}`);
      if (p.payCategoryName || p.payRateTemplateName)
        lines.push(`Pay category: ${p.payCategoryName || p.payRateTemplateName}`);
      if (p.payRate != null) lines.push(`Pay rate: $${p.payRate}${p.payUnit ? ` per ${p.payUnit}` : ""}`);
      if (p.classification) lines.push(`Classification: ${p.classification}`);
      if (p.awardName) lines.push(`Award (on file): ${p.awardName}`);
      lines.push(`KeyPay client: ${emp.client_name}`);
      lines.push("");
      lines.push("Please review this employee against Fair Work + the applicable modern award. Flag any compliance risks (under-payment, mis-classification, missing entitlements, casual conversion eligibility) and suggest concrete fixes.");
      setDescription(lines.join("\n"));
      setCurrent(null);
      setFollowUpMode(false);
      // Remember the picked worker so the ask body carries the
      // surname → the backend can redact any accidental typing that
      // includes it later.
      setPickedEmployee({
        first_name: emp.first_name || p.firstName || null,
        surname: emp.surname || p.surname || null,
      });
      // Scroll the textarea into view.
      setTimeout(() => {
        document.querySelector('[data-testid="fairwork-description"]')?.focus();
      }, 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load employee");
    }
  };

  // Apply search + active/terminated filter on the employee list.
  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    return employees.filter((e) => {
      if (!empShowTerminated && !e.is_active) return false;
      if (!q) return true;
      const hay = `${e.name} ${e.job_title || ""} ${e.client_name} ${e.employment_type || ""} ${e.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [employees, empSearch, empShowTerminated]);

  return (
    <div className="p-6 max-w-[1600px]" data-testid="fairwork-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#0F8B33]/10 text-[#0F8B33]">
          <Scale className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">FINANCE &amp; PAYROLL · FAIR WORK</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Fair Work HR Compliance</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Click a KeyPay employee on the left to pre-load their real data, or type a worker description below. The assistant references
            <a href="https://www.fairwork.gov.au/" target="_blank" rel="noreferrer"
              className="text-[#0F8B33] font-semibold underline mx-1 hover:no-underline">
              fairwork.gov.au<ExternalLink className="h-3 w-3 inline ml-0.5" />
            </a>
            and (when connected) the
            <a href="https://developer.fwc.gov.au/" target="_blank" rel="noreferrer"
              className="text-[#0F8B33] font-semibold underline mx-1 hover:no-underline">
              FWC Modern Awards Pay Database<ExternalLink className="h-3 w-3 inline ml-0.5" />
            </a>
            to flag compliance risks. Not legal advice — confirm complex cases with Fair Work Infoline (13 13 94).
          </p>
        </div>
        {/* FWC API status badge */}
        <button onClick={() => setFwcOpen((o) => !o)}
          data-testid="fairwork-fwc-toggle"
          className={`text-[10px] uppercase tracking-[0.15em] font-bold inline-flex items-center gap-1.5 px-2.5 h-8 rounded-sm border transition ${
            fwc?.configured && fwc?.enabled
              ? "border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
              : "border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
          }`}>
          {fwc?.configured && fwc?.enabled
            ? <ShieldCheck className="h-3.5 w-3.5" />
            : <ShieldAlert className="h-3.5 w-3.5" />}
          {fwc?.configured && fwc?.enabled ? "MAPD API · Connected" : "MAPD API · Not connected"}
        </button>
      </div>

      {fwcOpen && (
        <Card className="rounded-sm border-zinc-200 mb-4" data-testid="fairwork-fwc-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[300px]">
                <div className="label-overline mb-1">FWC Modern Awards Pay Database (MAPD) API</div>
                <p className="text-xs text-zinc-600 leading-relaxed mb-3">
                  Once you&apos;ve registered at
                  <a href="https://developer.fwc.gov.au/" target="_blank" rel="noreferrer"
                    className="text-[#0F8B33] underline mx-1">developer.fwc.gov.au<ExternalLink className="h-3 w-3 inline ml-0.5" /></a>
                  and subscribed to the MAPD API, paste your subscription primary key below. We&apos;ll then surface live minimum-wage / penalty / allowance rates inside the assistant&apos;s answers.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-2 mb-2">
                  <Input type="password" placeholder={fwc?.configured ? "•••• key already saved — leave blank to keep" : "Ocp-Apim-Subscription-Key"}
                    value={fwcKey}
                    onChange={(e) => setFwcKey(e.target.value)}
                    className="rounded-sm h-9 text-xs font-mono"
                    data-testid="fairwork-fwc-key" />
                  <Input type="url" value={fwcBaseUrl}
                    onChange={(e) => setFwcBaseUrl(e.target.value)}
                    placeholder="https://api.fwc.gov.au"
                    className="rounded-sm h-9 text-xs font-mono"
                    data-testid="fairwork-fwc-baseurl" />
                </div>
                <label className="text-[11px] inline-flex items-center gap-2 text-zinc-700 cursor-pointer mb-2">
                  <input type="checkbox" checked={fwcEnabled}
                    onChange={(e) => setFwcEnabled(e.target.checked)}
                    data-testid="fairwork-fwc-enabled" />
                  Use FWC data in compliance answers (when configured)
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" onClick={saveFwc} disabled={fwcSaving}
                    className="rounded-sm bg-[#0F8B33] hover:bg-[#0F8B33]/90"
                    data-testid="fairwork-fwc-save">
                    {fwcSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm"
                    onClick={testFwc} disabled={fwcSaving || !fwc?.configured}
                    data-testid="fairwork-fwc-test">
                    Test connection
                  </Button>
                  {fwc?.last_test_status != null && (
                    <span className="text-[10px] font-mono text-zinc-500">
                      last probe: {fwc.last_test_status}
                      {fwc.last_test_at ? ` · ${fwc.last_test_at.slice(0,16).replace("T", " ")}` : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_280px] gap-4">
        {/* Left column — KeyPay employee browser */}
        <div className="space-y-3">
          <Card className="rounded-sm border-zinc-200" data-testid="fairwork-employees">
            <CardContent className="p-0">
              <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700 flex items-center gap-1.5">
                <Users className="h-3 w-3" /> KeyPay employees
                <span className="ml-auto font-mono normal-case tracking-normal text-zinc-500">
                  {filteredEmployees.length}/{employees.length}
                </span>
                <button onClick={() => loadEmployees(true)}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Refresh roster"
                  data-testid="fairwork-employees-refresh">
                  <RefreshCw className={`h-3 w-3 ${employeesLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
              <div className="p-2 border-b border-zinc-100">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input value={empSearch}
                    onChange={(e) => setEmpSearch(e.target.value)}
                    placeholder="Search name / role / client…"
                    className="rounded-sm h-8 pl-7 text-xs"
                    data-testid="fairwork-employees-search" />
                </div>
                <label className="text-[10px] inline-flex items-center gap-1 mt-1.5 text-zinc-600 cursor-pointer">
                  <input type="checkbox" checked={empShowTerminated}
                    onChange={(e) => setEmpShowTerminated(e.target.checked)}
                    data-testid="fairwork-employees-show-terminated" />
                  Include terminated
                </label>
              </div>
              <div className="max-h-[65vh] overflow-y-auto divide-y divide-zinc-100">
                {employeesLoading && employees.length === 0 ? (
                  <div className="p-6 text-center text-xs text-zinc-500">
                    <Loader2 className="h-4 w-4 animate-spin inline" />
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="p-4 text-[11px] italic text-zinc-500 text-center">
                    {employees.length === 0
                      ? "No KeyPay clients connected yet."
                      : "No employees match this filter."}
                  </div>
                ) : (
                  filteredEmployees.map((emp) => (
                    <button key={`${emp.client_id}-${emp.employee_id}`}
                      onClick={() => pickEmployee(emp)}
                      data-testid={`fairwork-employee-${emp.employee_id}`}
                      className="w-full text-left px-3 py-2 hover:bg-[#0F8B33]/5 group">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold truncate">
                            {emp.name}
                            {!emp.is_active && (
                              <span className="ml-1.5 text-[9px] uppercase tracking-wider text-zinc-400 font-mono">ex</span>
                            )}
                          </div>
                          <div className="text-[10px] text-zinc-600 truncate">
                            {emp.job_title || <span className="italic text-zinc-400">No title</span>}
                            {emp.employment_type && (
                              <span className="ml-1 text-zinc-400">· {emp.employment_type}</span>
                            )}
                          </div>
                          <div className="text-[9px] font-mono text-zinc-400 truncate flex items-center gap-1 mt-0.5">
                            <Building2 className="h-2.5 w-2.5" /> {emp.client_name}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contractors register — under the KeyPay employees card */}
          <Card className="rounded-sm border-zinc-200" data-testid="fairwork-contractors">
            <CardContent className="p-0">
              <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700 flex items-center gap-1.5">
                <Briefcase className="h-3 w-3" /> Contractors
                <span className="ml-auto font-mono normal-case tracking-normal text-zinc-500">
                  {filteredContractors.length}/{contractors.length}
                </span>
                <button onClick={loadContractors}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Reload contractors"
                  data-testid="fairwork-contractors-refresh">
                  <RefreshCw className={`h-3 w-3 ${contractorsLoading ? "animate-spin" : ""}`} />
                </button>
                <button onClick={() => setContractorEditing({})}
                  className="text-zinc-400 hover:text-[#0F8B33]"
                  title="Add contractor"
                  data-testid="fairwork-contractor-add">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="p-2 border-b border-zinc-100 space-y-1.5">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input value={contractorSearch}
                    onChange={(e) => setContractorSearch(e.target.value)}
                    placeholder="Search business / contact / country…"
                    className="rounded-sm h-8 pl-7 text-xs"
                    data-testid="fairwork-contractors-search" />
                </div>
                {contractors.length > 0 && (
                  <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-1.5 pt-0.5">
                    <Globe className="h-2.5 w-2.5" />
                    R&D grant lens:
                    <span className="text-emerald-700">
                      {contractors.length - contractorOffshoreCount} AU
                    </span>
                    ·
                    <span className={contractorOffshoreCount > 0 ? "text-amber-700" : "text-zinc-500"}>
                      {contractorOffshoreCount} offshore
                    </span>
                  </div>
                )}
              </div>
              <div className="max-h-[55vh] overflow-y-auto divide-y divide-zinc-100">
                {contractorsLoading && contractors.length === 0 ? (
                  <div className="p-6 text-center text-xs text-zinc-500">
                    <Loader2 className="h-4 w-4 animate-spin inline" />
                  </div>
                ) : filteredContractors.length === 0 ? (
                  <div className="p-4 text-[11px] italic text-zinc-500 text-center">
                    {contractors.length === 0
                      ? "No contractors added yet. Click + to add one."
                      : "No contractors match this filter."}
                  </div>
                ) : (
                  filteredContractors.map((c) => {
                    const isAU = (c.country || "").trim().toLowerCase()
                      === "australia" || (c.country || "").trim().toLowerCase() === "au";
                    return (
                      <div key={c.contractor_id}
                        className="px-3 py-2 hover:bg-[#0F8B33]/5 group flex items-start gap-2"
                        data-testid={`fairwork-contractor-${c.contractor_id}`}>
                        <button onClick={() => pickContractor(c)}
                          className="flex-1 min-w-0 text-left"
                          data-testid={`fairwork-contractor-pick-${c.contractor_id}`}
                          title="Pre-fill compliance check with this contractor's data">
                          <div className="text-xs font-semibold truncate">
                            {c.business_name}
                            {!c.is_active && (
                              <span className="ml-1.5 text-[9px] uppercase tracking-wider text-zinc-400 font-mono">inactive</span>
                            )}
                          </div>
                          <div className="text-[10px] text-zinc-600 truncate">
                            {c.role || <span className="italic text-zinc-400">No role set</span>}
                          </div>
                          <div className="text-[9px] font-mono text-zinc-400 truncate flex items-center gap-1 mt-0.5">
                            <Globe className="h-2.5 w-2.5" />
                            <span className={isAU ? "text-emerald-700" : "text-amber-700"}>
                              {c.country || "—"}
                            </span>
                            {c.contact_first_name && (
                              <span className="ml-1 text-zinc-500 truncate">
                                · {c.contact_first_name}
                              </span>
                            )}
                          </div>
                        </button>
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100">
                          <button onClick={() => setContractorEditing(c)}
                            className="text-zinc-400 hover:text-[#0F8B33]"
                            title="Edit"
                            data-testid={`fairwork-contractor-edit-${c.contractor_id}`}>
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button onClick={() => deleteContractor(c.contractor_id)}
                            className="text-zinc-400 hover:text-rose-600"
                            title="Delete"
                            data-testid={`fairwork-contractor-delete-${c.contractor_id}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contractor editor dialog (add / edit) */}
        {contractorEditing !== null && (
          <ContractorEditor
            initial={contractorEditing}
            saving={contractorSaving}
            onCancel={() => setContractorEditing(null)}
            onSave={saveContractor} />
        )}

        {/* Main column — input + answer */}
        <div className="space-y-4">
          {!current ? (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="label-overline text-[10px] mb-1 flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" /> Describe the worker
                </div>
                <Textarea rows={8} value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={(tips.examples || [])[0] || "e.g. Sarah works 25h/wk Tue-Thu as receptionist at our Sydney dental clinic, casual, $32/h, 8 months in..."}
                  data-testid="fairwork-description"
                  className="rounded-sm font-sans" />

                {/* Prompt tips */}
                {(tips.tips || []).length > 0 && (
                  <div className="mt-3 p-3 rounded-sm border border-amber-200 bg-amber-50">
                    <div className="label-overline text-[10px] text-amber-900 flex items-center gap-1 mb-1.5">
                      <Lightbulb className="h-3 w-3" /> Include where possible
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-amber-900">
                      {(tips.tips || []).map((t) => (
                        <li key={t} className="flex gap-1.5">
                          <span className="text-amber-600">•</span>{t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end mt-3 pt-3 border-t border-zinc-200">
                  <Button onClick={ask} disabled={asking || polling}
                    className="rounded-sm bg-[#0F8B33] hover:bg-[#0F8B33]/90"
                    data-testid="fairwork-ask">
                    {(asking || polling) ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                    {polling ? "Thinking… (this can take 30-60s)" : asking ? "Sending…" : "Ask compliance assistant"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* The original question */}
              <Card className="rounded-sm border-zinc-200 bg-zinc-50">
                <CardContent className="p-4">
                  <div className="label-overline text-[10px] mb-1 flex items-center justify-between">
                    <span><MessageSquare className="h-3 w-3 inline mr-1" />Your description</span>
                    <button onClick={startNew}
                      className="text-[10px] normal-case tracking-normal text-zinc-500 hover:text-zinc-900"
                      data-testid="fairwork-new-thread">
                      ↺ Start new
                    </button>
                  </div>
                  <p className="text-sm whitespace-pre-wrap" data-testid="fairwork-question">{current.description}</p>
                  {current.follow_up && (
                    <div className="mt-3 pt-3 border-t border-zinc-200">
                      <div className="label-overline text-[10px] mb-1">Latest follow-up</div>
                      <p className="text-sm whitespace-pre-wrap text-zinc-700">{current.follow_up}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* The assistant's answer */}
              <Card className="rounded-sm border-zinc-200">
                <CardContent className="p-5">
                  <div className="label-overline text-[10px] mb-2 flex items-center justify-between">
                    <span><Scale className="h-3 w-3 inline mr-1" />Compliance guidance</span>
                    <span className="font-mono text-zinc-400 normal-case tracking-normal">
                      {current.status === "pending" ? "generating…" :
                       current.status === "error" ? "error" :
                       (() => {
                         const tot = (current.input_tokens || 0) + (current.output_tokens || 0);
                         const modelShort = current.model?.split("/")[1]?.split("-").slice(0, 3).join("-") || "";
                         return tot > 0 ? `${modelShort} · ${tot} tokens` : modelShort;
                       })()}
                    </span>
                  </div>
                  {current.status === "pending" ? (
                    <div className="flex items-center gap-2 text-sm text-zinc-500 py-6"
                      data-testid="fairwork-answer-pending">
                      <Loader2 className="h-4 w-4 animate-spin text-[#0F8B33]" />
                      Claude is researching the relevant award &amp; NES obligations… this can take 30-60 seconds.
                    </div>
                  ) : (
                    <div className={`prose prose-sm max-w-none text-sm whitespace-pre-wrap leading-relaxed ${current.status === "error" ? "text-red-700" : ""}`}
                      data-testid="fairwork-answer">
                      {current.answer}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Follow-up box */}
              <Card className="rounded-sm border-zinc-200">
                <CardContent className="p-4">
                  <div className="label-overline text-[10px] mb-1">Follow-up question</div>
                  <div className="flex gap-2">
                    <Textarea rows={2} value={followUp}
                      onChange={(e) => setFollowUp(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                      placeholder="e.g. What if we want to put her on a 6-month fixed-term contract instead?"
                      data-testid="fairwork-followup"
                      disabled={asking || polling || current.status === "pending"}
                      className="rounded-sm" />
                    <Button onClick={ask} disabled={asking || polling || current.status === "pending"}
                      className="rounded-sm bg-[#0F8B33] hover:bg-[#0F8B33]/90 shrink-0"
                      data-testid="fairwork-followup-send">
                      {(asking || polling) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Right column — history + examples */}
        <div className="space-y-4">
          <Card className="rounded-sm border-zinc-200" data-testid="fairwork-history">
            <CardContent className="p-0">
              <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-1.5">
                <History className="h-3 w-3" /> Recent queries
                <span className="ml-auto font-mono normal-case tracking-normal">{history.length}</span>
              </div>
              {history.length === 0 ? (
                <div className="p-4 text-xs italic text-zinc-500 text-center">No queries yet.</div>
              ) : (
                <div className="divide-y divide-zinc-100 max-h-[60vh] overflow-y-auto">
                  {history.map((q) => (
                    <button key={q.query_id}
                      onClick={() => loadFromHistory(q.query_id)}
                      data-testid={`fairwork-history-${q.query_id}`}
                      className={`w-full text-left px-3 py-2 hover:bg-zinc-50 group ${
                        current?.query_id === q.query_id ? "bg-emerald-50" : ""
                      }`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs line-clamp-2">{q.description}</div>
                          <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                            {(q.created_at || "").slice(0, 16).replace("T", " ")}
                          </div>
                        </div>
                        <button onClick={(e) => removeFromHistory(q.query_id, e)}
                          className="text-zinc-300 hover:text-red-600 opacity-0 group-hover:opacity-100"
                          title="Delete">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {!current && (tips.examples || []).length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-0">
                <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600 flex items-center gap-1.5">
                  <Lightbulb className="h-3 w-3" /> Example prompts
                </div>
                <div className="divide-y divide-zinc-100">
                  {(tips.examples || []).map((ex) => (
                    <button key={ex}
                      onClick={() => setDescription(ex)}
                      className="w-full text-left px-3 py-2 text-[11px] hover:bg-amber-50/50">
                      {ex}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-3">
              <div className="text-[10px] text-zinc-500 leading-relaxed">
                <strong>Not legal advice.</strong> Outputs are AI-generated guidance based on publicly available Fair Work
                Australia information. For high-risk decisions (termination, unfair-dismissal exposure, contractor
                disputes) confirm with a qualified employment lawyer or call Fair Work Infoline on <span className="font-mono">13 13 94</span>.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}



/**
 * ContractorEditor — modal dialog for adding / editing a contractor
 * in the Fair Work register. Country is required (needed for the R&D
 * grant workflow). Everything else optional but the more data the
 * admin captures the sharper the compliance-check answer will be.
 */
function ContractorEditor({ initial, saving, onCancel, onSave }) {
  const isEdit = !!(initial && initial.contractor_id);
  const [form, setForm] = useState({
    business_name: initial.business_name || "",
    contact_first_name: initial.contact_first_name || "",
    contact_surname: initial.contact_surname || "",
    country: initial.country || "Australia",
    state: initial.state || "",
    abn: initial.abn || "",
    role: initial.role || "",
    engagement_entity: initial.engagement_entity || "sole_trader",
    hours_per_week: initial.hours_per_week ?? "",
    pay_pattern: initial.pay_pattern || "",
    invoice_pattern: initial.invoice_pattern || "",
    uses_own_equipment: initial.uses_own_equipment ?? null,
    works_exclusively_for_us: initial.works_exclusively_for_us ?? null,
    engaged_since: initial.engaged_since || "",
    notes: initial.notes || "",
    is_active: initial.is_active !== false,
  });
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.business_name.trim() || form.business_name.trim().length < 2) {
      return toast.error("Business / trading name is required.");
    }
    if (!form.country.trim()) {
      return toast.error("Country is required (used for R&D grants).");
    }
    const payload = {
      ...form,
      hours_per_week: form.hours_per_week === ""
        ? null : Number(form.hours_per_week),
    };
    if (isEdit) payload.contractor_id = initial.contractor_id;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="fairwork-contractor-editor">
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-[#0F8B33]" />
          <h2 className="font-semibold text-sm">
            {isEdit ? "Edit contractor" : "Add contractor"}
          </h2>
          <span className="ml-auto text-[10px] font-mono text-zinc-500">
            {isEdit ? "" : "Register a contractor for compliance + R&D checks"}
          </span>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField label="Business / trading name *"
              value={form.business_name} onChange={setField("business_name")}
              testid="ce-business_name" required />
            <TextField label="Role / services"
              value={form.role} onChange={setField("role")}
              placeholder="e.g. Digital marketing consultant"
              testid="ce-role" />
            <TextField label="Contact first name"
              value={form.contact_first_name}
              onChange={setField("contact_first_name")}
              testid="ce-contact_first_name" />
            <TextField label="Contact surname (never sent to AI)"
              value={form.contact_surname}
              onChange={setField("contact_surname")}
              testid="ce-contact_surname" />
            <TextField label="Country *"
              value={form.country} onChange={setField("country")}
              placeholder="Australia · Philippines · India …"
              testid="ce-country" required />
            <TextField label="State / region"
              value={form.state} onChange={setField("state")}
              testid="ce-state" />
            <TextField label="ABN"
              value={form.abn} onChange={setField("abn")}
              testid="ce-abn" />
            <SelectField label="Entity type"
              value={form.engagement_entity}
              onChange={setField("engagement_entity")}
              options={[
                { v: "sole_trader", l: "Sole trader" },
                { v: "company", l: "Company (Pty Ltd)" },
                { v: "partnership", l: "Partnership" },
                { v: "individual", l: "Individual (no ABN)" },
                { v: "trust", l: "Trust" },
              ]}
              testid="ce-engagement_entity" />
            <TextField label="Hours/week (approx)"
              value={form.hours_per_week}
              onChange={(v) => setField("hours_per_week")(v.replace(/[^0-9.]/g, ""))}
              testid="ce-hours_per_week" />
            <TextField label="Engaged since (YYYY-MM-DD)"
              value={form.engaged_since}
              onChange={setField("engaged_since")}
              testid="ce-engaged_since" />
            <TextField label="Pay pattern"
              value={form.pay_pattern} onChange={setField("pay_pattern")}
              placeholder="$5,000/mo retainer · $120/h billed weekly"
              testid="ce-pay_pattern" wide />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-700">
              Invoice pattern
              <span className="ml-2 text-[10px] font-mono text-zinc-500 lowercase">
                sequential numbering? invoices us only? monthly retainer? — feeds sham-contracting risk check
              </span>
            </label>
            <Textarea rows={2} value={form.invoice_pattern}
              onChange={(e) => setField("invoice_pattern")(e.target.value)}
              className="rounded-sm text-xs mt-1"
              placeholder="e.g. Sequential invoice numbers 001, 002, 003 — appears to invoice only us."
              data-testid="ce-invoice_pattern" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TriBoolField label="Uses own equipment"
              value={form.uses_own_equipment}
              onChange={setField("uses_own_equipment")}
              testid="ce-uses_own_equipment" />
            <TriBoolField label="Works exclusively for us"
              value={form.works_exclusively_for_us}
              onChange={setField("works_exclusively_for_us")}
              testid="ce-works_exclusively_for_us" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-700">
              Notes
            </label>
            <Textarea rows={2} value={form.notes}
              onChange={(e) => setField("notes")(e.target.value)}
              className="rounded-sm text-xs mt-1"
              placeholder="Anything else the compliance assistant should know."
              data-testid="ce-notes" />
          </div>

          <label className="text-[11px] inline-flex items-center gap-1.5 text-zinc-700">
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => setField("is_active")(e.target.checked)}
              data-testid="ce-is_active" />
            Active engagement
          </label>
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50">
          <Button size="sm" variant="outline" onClick={onCancel}
            className="rounded-sm"
            data-testid="ce-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving}
            className="rounded-sm bg-[#0F8B33] hover:bg-[#0F8B33]/90"
            data-testid="ce-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            {isEdit ? "Save changes" : "Add contractor"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid, required, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-700">
        {label}
      </label>
      <Input value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-sm h-8 text-xs mt-1"
        data-testid={testid}
        required={required} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-700">
        {label}
      </label>
      <select value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-sm border border-zinc-200 h-8 px-2 text-xs mt-1 bg-white"
        data-testid={testid}>
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </div>
  );
}

function TriBoolField({ label, value, onChange, testid }) {
  const cur = value === true ? "yes"
    : value === false ? "no" : "";
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-700">
        {label}
      </label>
      <select value={cur}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "yes" ? true : v === "no" ? false : null);
        }}
        className="w-full rounded-sm border border-zinc-200 h-8 px-2 text-xs mt-1 bg-white"
        data-testid={testid}>
        <option value="">— unknown —</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );
}
